import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api from '../services/api';

interface Feature {
  icon: string;
  text: string;
}

interface Announcement {
  id: string;
  version: string;
  title: string;
  features: Feature[];
}

export function FeatureAnnouncement() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { socket } = useSocket();

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkAnnouncement = async () => {
      try {
        const response = await api.get('/announcements/current');
        if (response.data) {
          setAnnouncement(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch announcement:', error);
      }
    };

    // Beim Mount prüfen
    checkAnnouncement();

    // Alle 5 Minuten prüfen (Fallback)
    const interval = setInterval(checkAnnouncement, 300000);

    // WebSocket: Sofortiges Update bei neuem Announcement
    if (socket) {
      socket.on('new_announcement', () => {
        console.log('📢 New announcement received via WebSocket, checking if relevant for user...');
        // Nicht direkt setzen! Backend-API prüft targetAudience + isAdmin
        checkAnnouncement();
      });
    }

    return () => {
      clearInterval(interval);
      if (socket) {
        socket.off('new_announcement');
      }
    };
  }, [isAuthenticated, socket]);

  const handleDismiss = async () => {
    if (!announcement) return;

    setIsLoading(true);
    try {
      // Dismissiere Announcement
      await api.post(`/announcements/${announcement.id}/dismiss`);

      // Force Service Worker Update + Reload
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        
        if (registration.waiting) {
          // Neuer SW wartet bereits
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          
          // Warte auf controllerchange und reload
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
          });
        } else {
          // Trigger Update-Check
          await registration.update();
          
          // Warte kurz ob ein Update gefunden wird
          setTimeout(() => {
            if (registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              navigator.serviceWorker.addEventListener('controllerchange', () => {
                window.location.reload();
              });
            } else {
              // Kein Update verfügbar, reload trotzdem
              window.location.reload();
            }
          }, 1000);
        }
      } else {
        // Kein SW Support, einfach reloaden
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to dismiss announcement:', error);
      setIsLoading(false);
    }
  };

  if (!announcement) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9998,
          animation: 'fadeIn 0.3s ease-out'
        }}
        onClick={isLoading ? undefined : handleDismiss}
      />
      
      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '1rem',
          padding: '2rem',
          maxWidth: '90vw',
          width: '400px',
          boxShadow: 'var(--shadow-xl)',
          zIndex: 9999,
          animation: 'slideUp 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 0.5rem 0', color: 'var(--primary)' }}>
            {announcement.title}
          </h2>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
            Version {announcement.version}
          </div>
        </div>

        {/* Feature List */}
        <div style={{ marginBottom: '1.5rem' }}>
          {announcement.features.map((feature, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '0.75rem',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '0.5rem',
                marginBottom: index < announcement.features.length - 1 ? '0.5rem' : 0
              }}
            >
              <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{feature.icon}</span>
              <span style={{ fontSize: '1rem', lineHeight: '1.5', color: 'var(--text-secondary)' }}>
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        {/* Button */}
        <button
          onClick={handleDismiss}
          disabled={isLoading}
          style={{
            width: '100%',
            backgroundColor: 'var(--primary)',
            color: 'white',
            border: 'none',
            padding: '0.875rem',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? 'Wird geladen...' : 'Jetzt ausprobieren! 🚀'}
        </button>
      </div>
    </>
  );
}
